import React, { useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import logo from "../assets/logo.png";
import { PANEL, Spinner } from "./ui";

/**
 * The shell every signed-out screen sits in: the configured hero (image or
 * video, managed from the admin's Login / Register Images page) on the left,
 * and the form card on the right.
 *
 * All five auth screens rendered their own copy of this — including the
 * background loading states — so it lives here once. `title` is the heading
 * above the form, `subtitle` the line under it, and `action` an optional
 * control pinned to the top-right corner (Login's "Watch" button).
 */
const AuthLayout = ({ title, subtitle, action, children }) => {
  const { background, isBackgroundLoaded } = useContext(AuthContext);

  return (
    <div className="relative flex min-h-screen bg-gray-100 dark:bg-[#111112]">
      <div className="relative hidden w-0 overflow-hidden bg-[#161617] md:block md:w-2/3">
        {!isBackgroundLoaded ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#161617]">
            <Spinner className="h-8 w-8 text-orange-400" />
          </div>
        ) : background.type === "IMAGE" ? (
          <div
            className="h-full w-full bg-cover bg-center transition-opacity duration-500"
            style={{ backgroundImage: `url("${background?.url}")` }}
          />
        ) : (
          <video
            className="h-full w-full bg-cover bg-center object-cover transition-opacity duration-500"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          >
            <source src={background?.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}

        {/* Scrim + brand wordmark, so the hero never fights the artwork. */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute bottom-8 left-8 right-8">
          <div className="h-1 w-24 rounded-full bg-gradient-to-r from-red-500 to-orange-400" />
          <p className="mt-3 text-2xl font-extrabold tracking-tight text-white">VDP Tuning Team</p>
          <p className="text-sm text-white/70">Professional ECU file service</p>
        </div>
      </div>

      {action ? <div className="absolute right-4 top-4 z-20">{action}</div> : null}

      <div className="vdp-dot-grid relative flex w-full items-center justify-center p-4 md:w-1/3">
        <div className={`relative w-full max-w-md px-7 py-9 ${PANEL}`}>
          <div className="mb-6 flex flex-col items-center text-center">
            <img src={logo} alt="VDP Tuning" className="mb-3 h-12 w-12 rounded-[14px] object-contain" />
            <p className="bg-gradient-to-r from-red-600 to-orange-500 bg-clip-text text-xl font-extrabold text-transparent">
              VDP Tuning Team
            </p>
            {title ? (
              <h1 className="mt-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                {title}
              </h1>
            ) : null}
            {subtitle ? (
              <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">{subtitle}</p>
            ) : null}
          </div>

          {children}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
